import React, { useEffect } from 'react';
import styled from 'styled-components';
import { keyframes } from 'styled-components';
import { useLocation } from 'react-router-dom';

const BusinessConsultation = () => {
  const location = useLocation();
  const serviceType = location.state?.service || 'integration';

  // Titles based on service type
  const getServiceTitle = () => {
    switch(serviceType) {
      case 'integration':
        return 'AI Integration';
      case 'development':
        return 'Full AI Solution Development';
      case 'other':
        return 'AI Business Solutions';
      default:
        return 'Business Consultation';
    }
  };

  // Load Calendly widget script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  return (
    <ConsultationContainer>
      <ContentWrapper>
        <ConsultationHeader>
          <HeaderTitle>Schedule Your Free {getServiceTitle()} Consultation</HeaderTitle>
          <HeaderSubtitle>Let's discuss how we can help your business grow with AI</HeaderSubtitle>
        </ConsultationHeader>
        
        <InfoBox>
          <InfoTitle>About Your Business Consultation:</InfoTitle>
          <InfoText>
            During this consultation, our team will learn about your business needs, discuss potential 
            AI solutions, and help determine the best approach for your specific challenges. This is a 
            complimentary consultation with no obligation - we're here to provide genuine value and help 
            you understand how AI can transform your business.
          </InfoText>
          <InfoDetails>
            <InfoDetailItem>
              <InfoDetailIcon>⏱️</InfoDetailIcon>
              <InfoDetailText>Duration: 45 minutes</InfoDetailText>
            </InfoDetailItem>
            <InfoDetailItem>
              <InfoDetailIcon>💼</InfoDetailIcon>
              <InfoDetailText>With a senior business consultant</InfoDetailText>
            </InfoDetailItem>
            <InfoDetailItem>
              <InfoDetailIcon>💡</InfoDetailIcon>
              <InfoDetailText>Get custom recommendations for your business</InfoDetailText>
            </InfoDetailItem>
          </InfoDetails>
        </InfoBox>
        
        <CalendlyContainer>
          <div 
            className="calendly-inline-widget" 
            data-url="https://calendly.com/pmteamwinners/business-initial-consulting" 
            style={{ minWidth: '320px', height: '700px' }}
          />
        </CalendlyContainer>
      </ContentWrapper>
    </ConsultationContainer>
  );
};

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const ConsultationContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(to bottom, #f8f9fa, #e9ecef);
  display: flex;
  justify-content: center;
  padding: 40px 20px;
  
  @media (max-width: 768px) {
    padding: 30px 15px;
  }
`;

const ContentWrapper = styled.div`
  max-width: 1000px;
  width: 100%;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  padding: 40px;
  animation: ${fadeIn} 0.6s ease forwards;
  
  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`;

const ConsultationHeader = styled.div`
  text-align: center;
  margin-bottom: 30px;
`;

const HeaderTitle = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #024A5F;
  margin-bottom: 10px;
  
  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const HeaderSubtitle = styled.p`
  font-size: 18px;
  color: #6c757d;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const InfoBox = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 25px;
  margin-bottom: 30px;
  border-left: 4px solid #024A5F;
`;

const InfoTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #024A5F;
  margin-bottom: 15px;
`;

const InfoText = styled.p`
  font-size: 16px;
  line-height: 1.6;
  color: #495057;
  margin-bottom: 20px;
`;

const InfoDetails = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  
  @media (max-width: 576px) {
    flex-direction: column;
    gap: 15px;
  }
`;

const InfoDetailItem = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 200px;
`;

const InfoDetailIcon = styled.span`
  font-size: 24px;
  margin-right: 10px;
  
  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const InfoDetailText = styled.span`
  font-size: 15px;
  color: #495057;
`;

const CalendlyContainer = styled.div`
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e9ecef;
`;

export default BusinessConsultation; 